import { type RefObject } from 'react';

import type { SelectionExtensionPlugin } from '@atlaskit/editor-plugin-selection-extension/selection-extension-plugin-type';
import type {
	SelectionExtensionPluginOptions,
	ExtensionConfiguration,
} from '@atlaskit/editor-plugin-selection-extension/types';
import { conditionalHooksFactory } from '@atlaskit/platform-feature-flags-react/conditional-hooks-factory/conditional-hooks-factory';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types/next-editor-plugin';

import type { PageMode } from '@confluence/issue-create-side-panel/entry-points/IssueCreateSidePanelContextProvider.ts';
import { useEditorToolbarExtensions } from '@confluence/highlight-actions/entry-points/useEditorToolbarExtensions.ts';
import { useJiraWorkCreateSelectionExtensionV2 } from '@confluence/jira-work-create-extension/entry-points/useJiraWorkCreateSelectionExtensionV2.ts';
import { useNativeTabsSelectionExtension } from '@confluence/native-tabs/entry-points/useNativeTabsSelectionExtension.ts';
import type { RightSidebarContextType } from '@confluence/page-layout-context';
import { isExperimentEnabled } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';

import { useShouldFetchExternalExtensions } from './useShouldFetchExternalExtensions.ts';
import { useTrelloInboxSelectionExtension } from './useTrelloInboxSelectionExtension.tsx';

export const useSelectionExtensionPluginOptions = ({
	rightSidebarContextRef,
	pageModeRef,
	hasEditPermissionRef,
	contentId,
	editorAPIRef,
	flags,
	snippetExtensionList = [],
}: {
	rightSidebarContextRef: React.MutableRefObject<RightSidebarContextType>;
	pageModeRef: React.MutableRefObject<PageMode>;
	hasEditPermissionRef: React.MutableRefObject<boolean>;
	contentId: string | undefined;
	editorAPIRef: RefObject<PublicPluginAPI<[SelectionExtensionPlugin]> | null | undefined>;
	flags?: FlagsStateContainer;
	snippetExtensionList?: ExtensionConfiguration[];
}): SelectionExtensionPluginOptions => {
	const jiraWorkCreateV2Extension = useJiraWorkCreateSelectionExtensionV2({
		rightSidebarContextRef,
		pageModeRef,
		hasEditPermissionRef,
		editorAPIRef,
	});
	// During MAUI phase 3, hide Jira work-create only from the block menu while keeping it
	// available in the contextual toolbar.
	// eslint-disable-next-line confluence-feature-gating/no-preconditioning -- MAUI removal requires both the experiment and phase gate to be enabled.
	const shouldHideJiraWorkCreateBlockMenu =
		// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
		isExperimentEnabled('cc-maui-experiment') && fg('cc-maui-phase-3');
	const jiraWorkCreateV2ExtensionList = shouldHideJiraWorkCreateBlockMenu
		? jiraWorkCreateV2Extension.map((extension) => ({
				...extension,
				blockMenu: undefined,
			}))
		: jiraWorkCreateV2Extension;

	const shouldFetchExternalExtensions = useShouldFetchExternalExtensions({
		contentId,
		editorAPIRef,
	});
	const externalExtensions = useEditorToolbarExtensions(contentId, shouldFetchExternalExtensions);

	const useTrelloExtensionWithArgs = () => [
		useTrelloInboxSelectionExtension(contentId, editorAPIRef, flags),
	];
	const useConditionalTrelloExtension = conditionalHooksFactory(
		// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
		() => fg('quick_capture_trello_inbox'),
		useTrelloExtensionWithArgs,
		() => [] as ExtensionConfiguration[],
	);
	const trelloExtensionList = useConditionalTrelloExtension();
	const nativeTabsSelectionExtensionList = useNativeTabsSelectionExtension({ editorAPIRef });
	const extensionList = [
		...snippetExtensionList,
		...externalExtensions,
		...jiraWorkCreateV2ExtensionList,
		...trelloExtensionList,
		...nativeTabsSelectionExtensionList,
	];

	return {
		extensions: {},
		extensionList,
	};
};
